import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, newId } from '../engine/context'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, parse } from './c07.shared'
import {
  canWrite,
  type ImportCheck,
  type ImportRow,
  type ImportRowsBody,
  importFileOf,
  importRowsOf,
  importSummary,
  masterLog,
  num,
  registerEntity,
  requireWrite,
  text,
  type Usage,
  up,
  usageByRelations,
  usageFromCounts
} from './masterData.shared'

/**
 * Master Data produk — `/api/v1/wms/master/*`:
 * - MST-01 Master SKU FG (`mt_product`): `sku/list`, `sku/detail`, `sku/save`, `sku/import/preview`, `sku/import/commit`.
 * - MST-03 Master BOM per SKU (`mt_bill_of_material`, satu SKU = banyak baris): `bom/list`, `bom/options`, `bom/detail`,
 *   `bom/save`, `bom/import/preview`, `bom/import/commit`. BOM tidak punya header: aktif/nonaktif diturunkan dari
 *   `is_active` seluruh barisnya (ERD-EXTENSION D40 DERIVE).
 * Aktif/nonaktif dan hapus lewat endpoint umum `status` / `delete` (entitas `sku` dan `bom`).
 */

/* ============================================================ MST-01 Master SKU FG */

/** Level kemasan, bawah → atas. Pack selalu ada. */
export const SKU_LEVELS = ['Pack', 'Slop', 'Bal', 'Karton'] as const
type Level = (typeof SKU_LEVELS)[number]
const OPTIONAL_LEVELS: Level[] = ['Slop', 'Bal', 'Karton']

/** Pasangan konversi → kolom `mt_product`. */
const CONV_COLUMN: Record<string, string> = {
  'Pack>Slop': 'pack_per_slop',
  'Slop>Bal': 'slop_per_bal',
  'Bal>Karton': 'bal_per_karton',
  'Slop>Karton': 'slop_per_karton',
  'Pack>Bal': 'pack_per_bal',
  'Pack>Karton': 'pack_per_karton'
}
const CONV_DEFAULT: Record<string, number> = {
  'Pack>Slop': 10,
  'Slop>Bal': 20,
  'Bal>Karton': 4,
  'Slop>Karton': 50,
  'Pack>Bal': 200,
  'Pack>Karton': 800
}
/** Daftar TIPE SKU (v7.50): SKM, SKT dan SPM. Satu-satunya sumber untuk validasi simpan dan import; sama dengan `SKU_TYPES` di service-library (dicek uji). */
export const SKU_TYPES = ['SKM', 'SKT', 'SPM'] as const
const SKU_TYPE_ERROR = 'Tipe harus SKM, SKT, atau SPM.'
const KIND_LABEL: Record<string, string> = { FILTER: 'Filter', KRETEK: 'Kretek' }
const SKU_CODE = /^[A-Z0-9]{2,8}$/
const GTIN = /^\d{8,14}$/

const skuKode = (row: LocalRow) => String(row.sku_code ?? row.code)

/** Level yang dipakai satu SKU, bawah → atas. */
function levelsOf(row: LocalRow): Level[] {
  const listed = String(row.hierarchy ?? 'Karton>Bal>Slop>Pack')
    .split('>')
    .map(item => item.trim())
  return SKU_LEVELS.filter(level => level === 'Pack' || listed.includes(level))
}

const pairsOf = (levels: Level[]) => levels.slice(1).map((level, index) => `${levels[index]}>${level}`)

function conversionsOf(row: LocalRow, levels: Level[]) {
  return Object.fromEntries(
    pairsOf(levels).map(pair => [pair, num(row[CONV_COLUMN[pair]]) || CONV_DEFAULT[pair]])
  ) as Record<string, number>
}

/** Isi Karton (pack per karton) dihitung dari rantai konversi — bukan diketik. */
function packPerKarton(levels: Level[], conv: Record<string, number>): number | null {
  if (!levels.includes('Karton')) return null
  return pairsOf(levels).reduce((total, pair) => total * (conv[pair] || 0), 1)
}

type Dimension = { ok: true; value: string } | { ok: false }

/** Dimensi "P x L x T" (cm); menerima koma/titik dan x, ×, *. Kosong = tidak diisi. */
export function parseDimension(raw: unknown): Dimension | null {
  const input = text(raw)
  if (!input) return null
  const parts = input
    .replace(/,/g, '.')
    .split(/\s*[x×*]\s*/i)
    .map(item => item.trim())
  if (parts.length !== 3 || parts.some(item => !/^\d+(\.\d+)?$/.test(item))) return { ok: false }
  const values = parts.map(Number)
  if (values.some(value => !(value > 0))) return { ok: false }
  return { ok: true, value: values.map(value => value.toFixed(1).replace('.', ',')).join(' x ') }
}

const WRITE_SKU_MESSAGE = 'Menyunting SKU FG hanya untuk Admin WH FG.'

function skuUsedByLabels(id: string) {
  return (
    readTable('fg_labeler_batch').some(row => idOf(row.sku_id) === id) ||
    readTable('fg_label').some(row => idOf(row.sku_id) === id)
  )
}

function skuUsage(row: LocalRow): Usage {
  const rest = usageByRelations('mt_product', row.id, ['mt_bill_of_material'])
  const hasBom = readTable('mt_bill_of_material').some(line => idOf(line.sku_id) === row.id)
  if (!hasBom) return rest
  const items = [{ collection: 'mt_bill_of_material', label: 'BOM', count: 1 }, ...rest.items]
  return {
    total: rest.total + 1,
    items,
    sentence: items.map(item => `${item.count} ${item.label}`).join(', ')
  }
}

registerEntity('sku', {
  collection: 'mt_product',
  entity: 'SKU FG',
  page: 'MST-01',
  wh: 'FG',
  labelOf: row => skuKode(row),
  usage: skuUsage,
  deletable: true
})

const skuRow = (row: LocalRow) => {
  const levels = levelsOf(row)
  const conv = conversionsOf(row, levels)
  const kind = (row.cigarette_kind as string | null) ?? null
  return {
    id: row.id,
    kode: skuKode(row),
    code: String(row.code),
    name: String(row.name),
    type: String(row.type ?? ''),
    cigarette_kind: kind,
    kind_label: kind ? (KIND_LABEL[kind] ?? kind) : '—',
    btg_per_pack: num(row.btg_per_pack),
    gtin_pack: (row.gtin_pack as string | null) ?? null,
    levels,
    hierarchy: [...levels].reverse(),
    conversions: conv,
    pack_per_karton: packPerKarton(levels, conv),
    dimensions: (row.dimensions as Record<string, string> | undefined) ?? {},
    is_active: row.is_active !== false,
    inactive_reason: (row.inactive_reason as string | null) ?? null
  }
}

export function skuList() {
  const rows = readTable('mt_product')
    .slice()
    .sort((a, b) => skuKode(a).localeCompare(skuKode(b)))
    .map(skuRow)
  return {
    rows,
    total: rows.length,
    active: rows.filter(row => row.is_active).length,
    can_write: canWrite('MST-01')
  }
}

/** Contoh kode label JLC (segmen 4 = Kode SKU) dan nomor batch — memperlihatkan di mana Kode SKU dipakai. */
export function exampleCodes(kode: string, kind: string | null) {
  return {
    label: ['JLC', 'S', kind === 'KRETEK' ? 'K' : 'F', kode, '0042', '20261014', '101100', 'B001', '0017'].join(':'),
    batch: [kode, '20261014', '0042', 'B001'].join('-')
  }
}

export function skuDetail(params?: { id?: string }) {
  const row = findRow('mt_product', text(params?.id))
  if (!row) fail('SKU tidak ditemukan atau sudah tidak ada.', 'ERR_NOT_FOUND', 404)
  const base = skuRow(row)
  const usage = skuUsage(row)
  return {
    ...base,
    example: exampleCodes(base.kode, base.cigarette_kind),
    usage,
    code_locked: skuUsedByLabels(row.id),
    can_write: canWrite('MST-01')
  }
}

export type SkuSave = {
  id?: string
  kode?: string
  name?: string
  type?: string
  cigarette_kind?: string
  btg_per_pack?: number | string
  gtin_pack?: string
  levels?: Partial<Record<Level, boolean>>
  conversions?: Record<string, number | string>
  dimensions?: Partial<Record<Level, string>>
}

type SkuInput = {
  kode: string
  name: string
  type: string
  kind: string
  btg: number
  gtin: string
  levels: Level[]
  conv: Record<string, number>
  dims: Record<string, string>
}

const intOf = (value: unknown) => {
  const parsed = Number.parseInt(String(value ?? '').replace(/\D/g, ''), 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

/** Validasi + normalisasi isian SKU; mengembalikan daftar pesan per bidang (urut seperti form prototype). */
export function checkSku(body: SkuSave, ignoreId?: string): { input: SkuInput; errors: Record<string, string> } {
  const errors: Record<string, string> = {}
  const kode = up(body.kode)
  if (!kode) errors.kode = 'Kode SKU wajib diisi.'
  else if (!SKU_CODE.test(kode)) errors.kode = 'Kode SKU 2–8 huruf/angka, tanpa spasi.'
  else {
    const other = readTable('mt_product').find(
      row => row.id !== ignoreId && (up(skuKode(row)) === kode || up(row.code) === kode)
    )
    if (other) errors.kode = `Kode SKU sudah dipakai ${other.name}.`
  }
  const name = text(body.name)
  if (!name) errors.name = 'Nama merek wajib diisi.'
  const type = up(body.type)
  if (!type) errors.type = 'Pilih tipe.'
  else if (!(SKU_TYPES as readonly string[]).includes(type)) errors.type = SKU_TYPE_ERROR
  const kind = up(body.cigarette_kind)
  if (kind !== 'FILTER' && kind !== 'KRETEK') errors.cigarette_kind = 'Pilih Filter atau Kretek.'
  const btg = intOf(body.btg_per_pack)
  if (!(btg > 0)) errors.btg_per_pack = 'Isi jumlah batang per pack.'
  const gtin = text(body.gtin_pack)
  if (!GTIN.test(gtin)) errors.gtin_pack = 'GTIN Pack harus 8–14 digit angka.'
  const levels = SKU_LEVELS.filter(
    level => level === 'Pack' || (OPTIONAL_LEVELS.includes(level) && body.levels?.[level])
  )
  const conv: Record<string, number> = {}
  pairsOf([...levels]).forEach(pair => {
    conv[pair] = intOf(body.conversions?.[pair])
    if (!(conv[pair] > 0)) errors[`conv:${pair}`] = 'Isi angka lebih dari 0.'
  })
  const dims: Record<string, string> = {}
  levels.forEach(level => {
    const parsed = parseDimension(body.dimensions?.[level])
    if (parsed && !parsed.ok) errors[`dim:${level}`] = 'Format: panjang x lebar x tinggi.'
    else if (parsed) dims[level] = parsed.value
  })
  return { input: { kode, name, type, kind, btg, gtin, levels: [...levels], conv, dims }, errors }
}

function skuColumns(input: SkuInput) {
  const conv = Object.fromEntries(Object.values(CONV_COLUMN).map(column => [column, null as number | null])) as Record<
    string,
    number | null
  >
  pairsOf(input.levels).forEach(pair => {
    conv[CONV_COLUMN[pair]] = input.conv[pair]
  })
  return {
    name: input.name,
    type: input.type,
    cigarette_kind: input.kind,
    gtin_pack: input.gtin,
    btg_per_pack: input.btg,
    hierarchy: [...input.levels].reverse().join('>'),
    has_bal: input.levels.includes('Bal'),
    ...conv,
    dimensions: input.dims
  }
}

export function skuSave(body: SkuSave) {
  requireWrite('MST-01', WRITE_SKU_MESSAGE)
  const existing = body.id ? findRow('mt_product', body.id) : undefined
  if (body.id && !existing) fail('SKU tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const { input, errors } = checkSku(body, existing?.id)
  const first = Object.values(errors)[0]
  if (first) fail(first)
  if (existing) {
    if (input.kode !== up(skuKode(existing)) && skuUsedByLabels(existing.id))
      fail('Kode SKU dipakai di label dan batch, tidak bisa diubah.')
    const sameCode = String(existing.code) === skuKode(existing)
    const updated = updateRow('mt_product', existing.id, {
      ...auditFieldsForUpdate(),
      ...skuColumns(input),
      sku_code: input.kode,
      ...(sameCode ? { code: input.kode } : {})
    }) as LocalRow
    masterLog('FG', 'MST-01', 'mst-edit', input.kode, `Edit SKU FG ${input.kode}`)
    return skuRow(updated)
  }
  const created = insertRow('mt_product', {
    ...auditFieldsForCreate(),
    id: newId(),
    code: input.kode,
    sku_code: input.kode,
    ...skuColumns(input),
    is_active: true,
    inactive_reason: null
  })
  masterLog('FG', 'MST-01', 'mst-add', input.kode, `Tambah manual SKU FG ${input.kode}`)
  return skuRow(created)
}

/* ---------------------------------------------------------------- import SKU (berkas .xlsx sungguhan) */

export const SKU_IMPORT_FILE = 'Templat Import SKU FG.xlsx'
/** Judul kolom templat (sama dengan `SKU_IMPORT_COLUMNS` di service-library; dicek oleh uji). */
export const SKU_IMPORT_HEADERS = ['Kode SKU', 'Merek', 'Tipe', 'Jenis Rokok', 'Batang/Pack', 'Hierarki', 'GTIN Pack']
const HIERARCHY_SPLIT = /\s*(?:→|->|>|,|\/|\|)\s*/

type SkuImportVerdict = ImportCheck & { save?: SkuSave }

/** Satu baris berkas → verdict. `seen` = kode yang sudah muncul di baris atas berkas yang sama. */
function skuImportVerdict(raw: Record<string, string>, seen: Set<string>): SkuImportVerdict {
  const kode = up(raw['Kode SKU'])
  if (!kode) return { state: 'invalid', reason: 'Kode SKU wajib diisi' }
  const again = seen.has(kode)
  seen.add(kode)
  if (again) return { state: 'duplicate', reason: 'Kode dobel di berkas' }
  const name = text(raw.Merek)
  const existing = readTable('mt_product').find(row => up(skuKode(row)) === kode || up(row.code) === kode)
  if (existing) {
    return text(existing.name).toLowerCase() === name.toLowerCase()
      ? { state: 'duplicate', reason: 'Sudah ada' }
      : { state: 'invalid', reason: `Kode SKU dipakai ${text(existing.name)}` }
  }
  const tokens = text(raw.Hierarki)
    .split(HIERARCHY_SPLIT)
    .map(item => item.trim())
    .filter(Boolean)
  if (!tokens.length) return { state: 'invalid', reason: 'Hierarki wajib diisi' }
  const unknown = tokens.find(item => !SKU_LEVELS.some(level => level.toLowerCase() === item.toLowerCase()))
  if (unknown)
    return { state: 'invalid', reason: `Satuan "${unknown}" tak dikenal di Hierarki (Pack, Slop, Bal, Karton)` }
  const picked = SKU_LEVELS.filter(level => tokens.some(item => item.toLowerCase() === level.toLowerCase()))
  if (!picked.includes('Pack')) return { state: 'invalid', reason: 'Hierarki harus memuat Pack' }
  const kindRaw = up(raw['Jenis Rokok'])
  const save: SkuSave = {
    kode,
    name,
    type: raw.Tipe,
    cigarette_kind: kindRaw,
    btg_per_pack: raw['Batang/Pack'],
    gtin_pack: text(raw['GTIN Pack']),
    levels: Object.fromEntries(picked.filter(level => level !== 'Pack').map(level => [level, true])),
    conversions: Object.fromEntries(pairsOf(picked).map(pair => [pair, CONV_DEFAULT[pair]]))
  }
  const { errors } = checkSku(save)
  const first = Object.values(errors)[0]
  return first ? { state: 'invalid', reason: first.replace(/\.$/, '') } : { state: 'valid', save }
}

export function skuImportPreview(body: ImportRowsBody) {
  requireWrite('MST-01', WRITE_SKU_MESSAGE)
  const seen = new Set<string>()
  const rows: ImportRow[] = importRowsOf(body).map(({ n, raw }) => ({
    n,
    cells: [
      up(raw['Kode SKU']),
      raw.Merek,
      up(raw.Tipe),
      KIND_LABEL[up(raw['Jenis Rokok'])] ?? raw['Jenis Rokok'],
      raw['Batang/Pack'],
      raw.Hierarki,
      raw['GTIN Pack']
    ],
    ...skuImportVerdict(raw, seen)
  }))
  return importSummary(importFileOf(body, SKU_IMPORT_FILE), rows)
}

export function skuImportCommit(body: ImportRowsBody) {
  requireWrite('MST-01', WRITE_SKU_MESSAGE)
  const seen = new Set<string>()
  let imported = 0
  let skipped = 0
  let rejected = 0
  importRowsOf(body).forEach(({ raw }) => {
    const verdict = skuImportVerdict(raw, seen)
    if (verdict.state === 'duplicate') skipped += 1
    else if (verdict.state === 'invalid' || !verdict.save) rejected += 1
    else {
      const { input } = checkSku(verdict.save)
      insertRow('mt_product', {
        ...auditFieldsForCreate(),
        id: newId(),
        code: input.kode,
        sku_code: input.kode,
        ...skuColumns(input),
        is_active: true,
        inactive_reason: null
      })
      imported += 1
    }
  })
  if (!imported) fail('Tidak ada baris valid untuk diimport.')
  masterLog(
    'FG',
    'MST-01',
    'mst-import',
    importFileOf(body, SKU_IMPORT_FILE),
    `Import ${imported} SKU FG (dilewati ${skipped} duplikat, ditolak ${rejected})`
  )
  return { imported, skipped, rejected }
}

/* ============================================================ MST-03 Master BOM per SKU */

/** Urutan kategori mengikuti dokumen spesifikasi client, ditambah Lem dan Tembakau. */
export const BOM_CATEGORY_ORDER = [
  'etiket',
  'ctp',
  'papir',
  'filter',
  'kraft',
  'foil',
  'inner',
  'opp',
  'teartape',
  'lem',
  'tembakau'
]

const WRITE_BOM_MESSAGE = 'Menyunting BOM hanya untuk Admin WH RM.'
const linesOf = (skuId: string) => readTable('mt_bill_of_material').filter(line => idOf(line.sku_id) === skuId)
const lineActive = (line: LocalRow) => line.is_active !== false

const categoryOf = (material: LocalRow | undefined) => {
  const group = material ? findRow('mt_material_group', idOf(material.category_id)) : undefined
  return { code: String(group?.code ?? ''), name: String(group?.name ?? '') }
}

function bomUsage(row: LocalRow): Usage {
  const requests = readTable('material_request').filter(request => idOf(request.sku_id) === row.id).length
  return usageFromCounts([['SPK memakai merek ini', requests]])
}

registerEntity('bom', {
  collection: 'mt_product',
  entity: 'Spesifikasi',
  page: 'MST-03',
  wh: 'RM',
  labelOf: row => skuKode(row),
  usage: bomUsage,
  deletable: true,
  setActive: (row, body) => {
    const lines = linesOf(row.id)
    if (!lines.length) fail('Spesifikasi tidak ditemukan.', 'ERR_NOT_FOUND', 404)
    lines.forEach(line =>
      updateRow('mt_bill_of_material', line.id, {
        ...auditFieldsForUpdate(),
        is_active: body.is_active,
        inactive_reason: body.is_active ? null : body.reason
      })
    )
    masterLog(
      'RM',
      'MST-03',
      body.is_active ? 'mst-activate' : 'mst-deactivate',
      skuKode(row),
      `${body.is_active ? 'Aktifkan Kembali' : 'Nonaktifkan'} spesifikasi ${skuKode(row)}${body.reason ? ` — alasan: ${body.reason}` : ''}`
    )
    return findRow('mt_product', row.id) as LocalRow
  },
  remove: row => {
    linesOf(row.id).forEach(line => deleteRow('mt_bill_of_material', line.id))
    masterLog('RM', 'MST-03', 'mst-delete', skuKode(row), `Hapus spesifikasi ${skuKode(row)} — belum terpakai`)
  }
})

function bomSummary(skuRowData: LocalRow) {
  const lines = linesOf(skuRowData.id)
  const byCategory = new Map<string, string[]>()
  lines.forEach(line => {
    const material = findRow('mt_material', idOf(line.material_id))
    const category = categoryOf(material)
    byCategory.set(category.name, [...(byCategory.get(category.name) ?? []), String(material?.code ?? '')])
  })
  const filled = BOM_CATEGORY_ORDER.map(code =>
    readTable('mt_material_group').find(group => up(group.code) === up(code))
  )
    .filter((group): group is LocalRow => Boolean(group))
    .filter(group => byCategory.has(String(group.name)))
  const total = readTable('mt_material_group').length
  const active = lines.some(lineActive)
  const reason = lines.find(line => !lineActive(line))?.inactive_reason as string | null | undefined
  return {
    id: skuRowData.id,
    kode: skuKode(skuRowData),
    code: String(skuRowData.code),
    name: String(skuRowData.name),
    material_count: lines.length,
    category_filled: filled.length,
    category_total: total,
    not_applicable: total - filled.length,
    categories: filled.map(group => ({
      code: String(group.code),
      name: String(group.name),
      materials: byCategory.get(String(group.name)) ?? []
    })),
    has_tobacco: byCategory.has('Tembakau'),
    is_active: active,
    inactive_reason: active ? null : (reason ?? null)
  }
}

export function bomList() {
  const skuIds = [...new Set(readTable('mt_bill_of_material').map(line => idOf(line.sku_id)))]
  const rows = skuIds
    .map(id => findRow('mt_product', id))
    .filter((row): row is LocalRow => Boolean(row))
    .sort((a, b) => skuKode(a).localeCompare(skuKode(b)))
    .map(bomSummary)
  return {
    rows,
    total: rows.length,
    active: rows.filter(row => row.is_active).length,
    can_write: canWrite('MST-03')
  }
}

/** Pilihan form: merek dari Master SKU FG (aktif) + material aktif per kategori (urutan dokumen client). */
export function bomOptions() {
  const withBom = new Set(readTable('mt_bill_of_material').map(line => idOf(line.sku_id)))
  const skus = readTable('mt_product')
    .filter(row => row.is_active !== false)
    .sort((a, b) => skuKode(a).localeCompare(skuKode(b)))
    .map(row => ({
      id: row.id,
      kode: skuKode(row),
      code: String(row.code),
      name: String(row.name),
      has_bom: withBom.has(row.id)
    }))
  const materials = readTable('mt_material').filter(row => row.is_active !== false)
  const categories = BOM_CATEGORY_ORDER.map(code =>
    readTable('mt_material_group').find(group => up(group.code) === up(code))
  )
    .filter((group): group is LocalRow => Boolean(group))
    .map(group => ({
      code: String(group.code),
      name: String(group.name),
      materials: materials
        .filter(material => idOf(material.category_id) === group.id)
        .sort((a, b) => String(a.code).localeCompare(String(b.code)))
        .map(material => ({
          id: material.id,
          code: String(material.code),
          name: String(material.name),
          variant: (material.variant as string | null) ?? null
        }))
    }))
    .filter(category => category.materials.length)
  return { skus, categories }
}

const findSku = (key: unknown) => {
  const value = text(key)
  return (
    findRow('mt_product', value) ??
    readTable('mt_product').find(row => row.code === value || up(skuKode(row)) === up(value))
  )
}

export function bomDetail(params?: { sku?: string; id?: string }) {
  const row = findSku(params?.id ?? params?.sku)
  if (!row || !linesOf(row.id).length) fail('Spesifikasi tidak ditemukan atau sudah tidak ada.', 'ERR_NOT_FOUND', 404)
  const summary = bomSummary(row)
  return {
    ...summary,
    material_ids: linesOf(row.id).map(line => idOf(line.material_id)),
    usage: bomUsage(row),
    can_write: canWrite('MST-03')
  }
}

export function bomSave(body: { sku_id?: string; material_ids?: string[] }) {
  requireWrite('MST-03', WRITE_BOM_MESSAGE)
  const row = body.sku_id ? findRow('mt_product', body.sku_id) : undefined
  if (!row) fail('Merek wajib dipilih.')
  const wanted = [...new Set((body.material_ids ?? []).map(text).filter(Boolean))]
  if (!wanted.length) fail('Pilih minimal satu material.')
  const materials = wanted.map(id => findRow('mt_material', id))
  if (materials.some(material => !material)) fail('Material tidak dikenal.')
  const current = linesOf(row.id)
  const active = current.length ? current.some(lineActive) : true
  const reason = (current.find(line => !lineActive(line))?.inactive_reason as string | null | undefined) ?? null
  current
    .filter(line => !wanted.includes(idOf(line.material_id)))
    .forEach(line => deleteRow('mt_bill_of_material', line.id))
  wanted
    .filter(id => !current.some(line => idOf(line.material_id) === id))
    .forEach(id => {
      const material = findRow('mt_material', id) as LocalRow
      insertRow('mt_bill_of_material', {
        ...auditFieldsForCreate(),
        id: newId(),
        sku_id: row.id,
        material_id: id,
        qty_per_unit: 1,
        unit_id: material.default_purchase_unit_id ?? null,
        is_active: active,
        inactive_reason: active ? null : reason
      })
    })
  const categories = new Set(materials.map(material => categoryOf(material as LocalRow).name))
  masterLog(
    'RM',
    'MST-03',
    'bom-save',
    skuKode(row),
    `Spesifikasi ${skuKode(row)} disimpan — ${wanted.length} material di ${categories.size} kategori`
  )
  return bomSummary(row)
}

/* ---------------------------------------------------------------- import BOM (berkas .xlsx sungguhan) */

export const BOM_IMPORT_FILE = 'Templat Import Spesifikasi BOM.xlsx'
export const BOM_IMPORT_HEADERS = ['Merek', 'Material']

type BomImportVerdict = ImportCheck & { product?: LocalRow; material?: LocalRow }

function bomImportVerdict(raw: Record<string, string>, seen: Set<string>): BomImportVerdict {
  const skuKey = text(raw.Merek)
  const materialCode = text(raw.Material)
  if (!skuKey) return { state: 'invalid', reason: 'Merek wajib diisi' }
  if (!materialCode) return { state: 'invalid', reason: 'Material wajib diisi' }
  const product =
    findSku(skuKey) ?? readTable('mt_product').find(row => text(row.name).toLowerCase() === skuKey.toLowerCase())
  if (!product) return { state: 'invalid', reason: 'Merek tak dikenal' }
  const material = readTable('mt_material').find(row => up(row.code) === up(materialCode))
  if (!material) return { state: 'invalid', reason: `Material ${materialCode} tak dikenal` }
  const key = `${String(product.id)}|${String(material.id)}`
  const again = seen.has(key)
  seen.add(key)
  if (again) return { state: 'duplicate', reason: 'Baris dobel di berkas' }
  const exists = linesOf(product.id).some(line => idOf(line.material_id) === material.id)
  return exists
    ? { state: 'duplicate', reason: 'Sudah ada di spesifikasi merek ini' }
    : { state: 'valid', product, material }
}

export function bomImportPreview(body: ImportRowsBody) {
  requireWrite('MST-03', WRITE_BOM_MESSAGE)
  const seen = new Set<string>()
  const rows: ImportRow[] = importRowsOf(body).map(({ n, raw }) => {
    const verdict = bomImportVerdict(raw, seen)
    return {
      n,
      cells: [raw.Merek, verdict.material ? categoryOf(verdict.material).name : '—', raw.Material],
      state: verdict.state,
      reason: verdict.reason
    }
  })
  return importSummary(importFileOf(body, BOM_IMPORT_FILE), rows)
}

export function bomImportCommit(body: ImportRowsBody) {
  requireWrite('MST-03', WRITE_BOM_MESSAGE)
  const seen = new Set<string>()
  let imported = 0
  let skipped = 0
  let rejected = 0
  importRowsOf(body).forEach(({ raw }) => {
    const verdict = bomImportVerdict(raw, seen)
    if (verdict.state === 'duplicate') skipped += 1
    else if (verdict.state === 'invalid' || !verdict.product || !verdict.material) rejected += 1
    else {
      insertRow('mt_bill_of_material', {
        ...auditFieldsForCreate(),
        id: newId(),
        sku_id: verdict.product.id,
        material_id: verdict.material.id,
        qty_per_unit: 1,
        unit_id: verdict.material.default_purchase_unit_id ?? null,
        is_active: true,
        inactive_reason: null
      })
      imported += 1
    }
  })
  if (!imported) fail('Tidak ada baris valid untuk diimport.')
  masterLog(
    'RM',
    'MST-03',
    'mst-import',
    importFileOf(body, BOM_IMPORT_FILE),
    `Import ${imported} BOM (dilewati ${skipped} duplikat, ditolak ${rejected})`
  )
  return { imported, skipped, rejected }
}

/* ============================================================ register */

export function registerMasterProductHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/master'
  const get = (path: string, run: (params: Record<string, unknown> | undefined) => unknown) =>
    mock
      .onGet(`${base}/${path}`)
      .reply(config => handle(() => run(config.params as Record<string, unknown> | undefined)))
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  get('sku/list', () => skuList())
  get('sku/detail', params => skuDetail(params as { id?: string }))
  post('sku/save', skuSave)
  post<ImportRowsBody>('sku/import/preview', skuImportPreview)
  post<ImportRowsBody>('sku/import/commit', skuImportCommit)
  get('bom/list', () => bomList())
  get('bom/options', () => bomOptions())
  get('bom/detail', params => bomDetail(params as { sku?: string; id?: string }))
  post('bom/save', bomSave)
  post<ImportRowsBody>('bom/import/preview', bomImportPreview)
  post<ImportRowsBody>('bom/import/commit', bomImportCommit)
}
